import { useEffect } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { SortDirection, TransactionSort } from "@finances/shared";
import { z } from "zod";
import { AuthGate, NotFound } from "./app/AuthGate.js";
import { Shell } from "./app/Shell.js";
import { Dashboard } from "./features/dashboard/Dashboard.js";

/**
 * **Every screen but the dashboard is loaded on demand** — docs/05 §5.6.
 *
 * The dashboard is what opening the app means, so it is in the entry chunk and
 * everything else is not. `defaultPreload: "intent"` fetches a screen's chunk
 * when a link is hovered or focused, so the split costs a wait only for
 * someone who navigates without touching a link first.
 *
 * `/ask` is the reason this stopped being optional in M4: the conversational
 * view is the heaviest screen in the app and the least often opened first.
 * `tools/check-bundle-size.mjs` measures the dashboard route rather than the
 * whole output, so the budget now means what docs/05 says it means.
 */
const lazy = <T extends { [K in N]: unknown }, N extends string>(load: () => Promise<T>, name: N) =>
  lazyRouteComponent(load, name);

/**
 * Search params are typed and validated, because a filtered transaction list
 * is a URL someone sends to the other person in the household — and a URL is
 * an input like any other.
 */
const MonthSearch = z.object({ month: z.string().optional() });

/**
 * A month, or one of the picker's ranges — Dashboard and Categories, which
 * always show a scope rather than tolerating "none". Budgets and Insights
 * stay on `MonthSearch`: a budget is dated to a month, and Insights' history
 * window is not widened by picking a range.
 */
/**
 * `categoryId` arrives from a "Set budget" offer on a transaction row: the
 * screen opens with that category's editor already up, so the offer lands on
 * the thing it offered rather than on a page to go looking through.
 */
const BudgetSearch = MonthSearch.extend({
  categoryId: z.string().optional(),
  /** Absent for tracking, the page's original view, so its old links still land there. */
  view: z.enum(["plan"]).optional(),
});

const RANGE_PERIODS = ["last-3-months", "last-6-months", "last-12-months", "all-time"] as const;
const PeriodSearch = z.object({
  month: z.string().optional(),
  period: z.enum(RANGE_PERIODS).optional(),
});

/**
 * The dashboard also carries which accounts its figures are about: ids
 * separated by commas, absent for all of them. A string rather than an array
 * so the URL stays something a person can read and send — see
 * `features/dashboard/focus.ts`.
 */
const DashboardSearch = PeriodSearch.extend({ accountIds: z.string().optional() });

/** `c` is the conversation being read. Short, because it is in every URL. */
const AskSearch = z.object({ c: z.string().optional() });

const TransactionSearch = z.object({
  month: z.string().optional(),
  /** Transactions already has "every month" — a bare month/period, both
   *  absent — so only the three bounded ranges are offered here. */
  period: z.enum(["last-3-months", "last-6-months", "last-12-months"]).optional(),
  accountId: z.string().optional(),
  categoryId: z.string().optional(),
  q: z.string().optional(),
  direction: z.enum(["in", "out"]).optional(),
  uncategorised: z.boolean().optional(),
  /**
   * The review queue: transfers with no matching payment elsewhere in the ledger
   * and nobody's answer on them, which are therefore in none of the totals.
   */
  unclassifiedTransfer: z.boolean().optional(),
  /** Show each row against its category's budget for that month. */
  budget: z.boolean().optional(),
  /**
   * Which column the list is in order of, and which way. Both absent is the
   * ledger's own order, newest first — so the ordinary URL stays the short one.
   */
  sort: TransactionSort.optional(),
  dir: SortDirection.optional(),
});

const DuplicateSearch = z.object({
  month: z.string().optional(),
  confidence: z.enum(["likely", "possible", "unlikely"]).optional(),
});

/** Narrows which entries show; pairing itself is always computed over the
 *  whole ledger so a pair split across a month boundary still shows as one. */
const TransfersSearch = z.object({ month: z.string().optional() });

const ReviewSearch = z.object({
  reason: z.enum(["disagrees-with-rule", "mirrors-a-transfer"]).optional(),
});

/**
 * Three routes live outside the session — an invitation link, the security
 * page, and the design page — so the decision is made here rather than by a
 * pathless layout route.
 *
 * A layout route would be the idiomatic answer, but it puts its own id into
 * every child's route id, which every `useSearch({ from })` then has to spell
 * out. A flat tree keeps `from: "/transactions"` readable, and this is one
 * legible line.
 *
 * `/security` has to be here rather than behind `AuthGate`: it answers the
 * question someone asks *before* they have an account, and `AuthGate` shows a
 * sign-in form for every path but "/" when signed out.
 */
function Root() {
  const path = useRouterState({ select: (state) => state.location.pathname });
  const outsideTheSession =
    path.startsWith("/invite/") || path === "/security" || path === "/design";

  if (outsideTheSession) return <Outlet />;

  return (
    <AuthGate>
      <Shell />
    </AuthGate>
  );
}

const rootRoute = createRootRoute({ component: Root, notFoundComponent: NotFound });

/**
 * Accepting an invitation is deliberately outside the shell: the person
 * opening it may have no household yet, which is exactly the state the shell
 * assumes away.
 */
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  component: lazy(() => import("./features/auth/AcceptInvite.js"), "AcceptInvite"),
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: DashboardSearch,
  component: Dashboard,
});

/**
 * Where the homepage's "Log in" link points. `AuthGate` shows the sign-in
 * form for any path but `/` while signed out, so this route only has work to
 * do once sign-in succeeds: send the now-authenticated visitor on to the
 * dashboard rather than leaving them on a page with no form left on it.
 */
function LoginRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    void navigate({ to: "/" });
  }, [navigate]);
  return null;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginRedirect,
});

const transactionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/transactions",
  validateSearch: TransactionSearch,
  component: lazy(() => import("./features/transactions/Transactions.js"), "Transactions"),
});

const transfersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/transfers",
  validateSearch: TransfersSearch,
  component: lazy(() => import("./features/transfers/Transfers.js"), "Transfers"),
});

const duplicatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/duplicates",
  validateSearch: DuplicateSearch,
  component: lazy(() => import("./features/duplicates/Duplicates.js"), "Duplicates"),
});

const reviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/review",
  validateSearch: ReviewSearch,
  component: lazy(() => import("./features/review/Review.js"), "Review"),
});

const historyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/history",
  component: lazy(() => import("./features/history/History.js"), "History"),
});

const importRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/import",
  component: lazy(() => import("./features/import/Import.js"), "Import"),
});

const accountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accounts",
  component: lazy(() => import("./features/accounts/Accounts.js"), "Accounts"),
});

const categoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/categories",
  validateSearch: PeriodSearch,
  component: lazy(() => import("./features/categories/Categories.js"), "Categories"),
});

const budgetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/budgets",
  validateSearch: BudgetSearch,
  component: lazy(() => import("./features/budgets/Budgets.js"), "Budgets"),
});

const insightsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/insights",
  validateSearch: MonthSearch,
  component: lazy(() => import("./features/insights/Insights.js"), "Insights"),
});

const askRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ask",
  validateSearch: AskSearch,
  component: lazy(() => import("./features/ask/Ask.js"), "Ask"),
});

const rulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/rules",
  component: lazy(() => import("./features/rules/Rules.js"), "Rules"),
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: lazy(() => import("./features/settings/Settings.js"), "Settings"),
});

/**
 * Public, and lazily loaded like every other non-dashboard screen: it is read
 * once before signing up and rarely again.
 */
const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/security",
  component: lazy(() => import("./features/marketing/Security.js"), "Security"),
});

/**
 * Development builds only. It is a page in the real app using the real
 * stylesheet, not a separate Storybook build that can drift from it.
 */
const designRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/design",
  component: lazy(() => import("./routes/design.js"), "DesignPage"),
});

const screens = [
  dashboardRoute,
  loginRoute,
  transactionsRoute,
  transfersRoute,
  duplicatesRoute,
  reviewRoute,
  historyRoute,
  importRoute,
  accountsRoute,
  categoriesRoute,
  budgetsRoute,
  insightsRoute,
  askRoute,
  rulesRoute,
  settingsRoute,
  inviteRoute,
  securityRoute,
];

const routeTree = rootRoute.addChildren(import.meta.env.DEV ? [...screens, designRoute] : screens);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
